import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_CONFIG, API_ENDPOINTS } from '../config/apiConfig';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import ProductCard from '../components/ProductCard';

const ProductDetailPage = () => {
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState('/assets/img/product/big-product1.jpg');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [variations, setVariations] = useState([]);

  const { id } = useParams();
  const productId = id;
  const cart = useCart();
  const wishlist = useWishlist();

  // Check if product is in cart
  const isInCart = cart.isInCart(product?.id);
  const cartItem = isInCart ? cart.cart.find(item => item.id === product.id) : null;

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.PRODUCTS.GET_BY_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(productId) })
        });
        if (!res.ok) throw new Error(`HTTP Error ${res.status}`);
        const json = await res.json();
        if (!json || !json.data) throw new Error('Invalid product data');
        const data = json.data;
        // Parse variations
        let parsedVariations = [];
        try {
          if (data.variations && data.variations !== '[]' && data.variations !== '') {
            parsedVariations = JSON.parse(data.variations);
            // Normalize field names: use 'title' as 'name' for consistency
            parsedVariations = parsedVariations.map(group => ({
              ...group,
              name: group.title,
              items: group.items.map(item => ({
                ...item,
                name: item.title,
                id: item.title // use title as id for selection
              }))
            }));
          }
        } catch (err) {
          console.warn('Failed to parse variations:', err);
          parsedVariations = [];
        }
        
        // Parse images
        let images = [];
        try {
          if (data.images && data.images !== '[]' && data.images !== '') {
            const parsed = JSON.parse(data.images);
            // Filter out empty strings
            images = parsed.filter(img => img && img.trim() !== '');
          }
        } catch (err) {
          console.warn('Failed to parse images:', err);
          images = [];
        }
        
        setVariations(parsedVariations);
        
        // Set default selected variation (first item of first variation group)
        if (parsedVariations.length > 0 && parsedVariations[0].items && parsedVariations[0].items.length > 0) {
          setSelectedVariation(parsedVariations[0].items[0]);
        }
        // Build product object
        const prod = {
          id: data.id,
          name: data.name,
          cover: data.cover,
          images: images, // Already parsed array of image filenames
          original_price: parseFloat(data.original_price) || 0,
          sell_price: parseFloat(data.sell_price) || parseFloat(data.original_price) || 0,
          discount: parseFloat(data.discount) || 0,
          rating: data.rating || 0,
          reviews: data.total_rating || 0,
          description: data.descriptions || '',
          // normalize in_stock to number (API uses `in_stoke`)
          in_stock: Number(data.in_stoke ?? data.in_stock ?? 0),
          // include feature & disclaimer fields
          key_features: data.key_features || null,
          disclaimer: data.disclaimer || null,
          soldby: json.soldby || null,
          related: json.related || [],
          have_pcs: Number(data.have_pcs),
          pcs: data.pcs,
          have_kg: Number(data.have_kg),
          kg: data.kg,
          have_gram: Number(data.have_gram),
          gram: data.gram,
          have_liter: Number(data.have_liter),
          liter: data.liter,
          have_ml: Number(data.have_ml),
          ml: data.ml,
          variations: parsedVariations,
          exp_date: data.exp_date,
          type_of: data.type_of,
          in_offer: data.in_offer,
          kind: data.kind,
          cate_id: data.cate_id,
          sub_cate_id: data.sub_cate_id,
          store_id: data.store_id
        };
        setProduct(prod);
        
        // Build image array for display: [cover, ...images]
        const displayImages = [];
        
        // Add cover as first image
        if (prod.cover && prod.cover.trim()) {
          displayImages.push(prod.cover.startsWith('http') 
            ? prod.cover 
            : `${API_CONFIG.IMAGE_URL}${prod.cover}`);
        }
        
        // Add additional images
        if (prod.images && prod.images.length > 0) {
          prod.images.forEach(img => {
            if (img && img.trim()) {
              displayImages.push(img.startsWith('http') 
                ? img 
                : `${API_CONFIG.IMAGE_URL}${img}`);
            }
          });
        }
        
        // Set selected image (first available or default)
        const firstImage = displayImages.length > 0 
          ? displayImages[0] 
          : '/assets/img/product/big-product1.jpg';
        setSelectedImage(firstImage);
      } catch (err) {
        console.error('Error fetching product details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Using product state from API response

  // Debug: log product to check measurement fields
  if (product) {
    // eslint-disable-next-line
    console.log('Product for measurement:', product);
  }

  return (
    <>
      <Header />
      <main className="main__content_wrapper">
        <style>{` 
          .pd-wrap { padding: 40px 0; }
          .pd-grid { display: flex; gap: 24px; align-items: flex-start; }
          .pd-left { flex: 1 1 48%; }
          .pd-right { flex: 1 1 48%; }
          .pd-card { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 2px 8px rgba(15,23,42,0.03); }
          .pd-image { width: 400px; height: 400px; border-radius: 8px; object-fit: cover; }
          .pd-thumbs { display:flex; gap:8px; margin-top:12px; }
          .pd-thumb { width:56px; height:56px; border-radius:8px; object-fit:cover; cursor:pointer; border:1px solid #eee; }
          .pd-title { font-size: 26px; font-weight: 600; margin:0 0 12px 0; }
          .pd-selected-variation { color: #0b63d6; font-weight: 500; }
          .pd-price-row { display:flex; align-items:center; gap:12px; margin-bottom:12px; }
          .pd-price { font-size:24px; font-weight:700; color:#111; }
          .pd-old { text-decoration: line-through; color:#9AA0A6; }
          .pd-available { font-weight:600; }
          .pd-measure { display:inline-block; margin-left:6px; padding:6px 12px; border-radius:18px; border:1px solid #e6eefc; color:#0b63d6; background:#f5fbff; }
          .pd-qty { display:flex; align-items:center; gap:8px; }
          .pd-qty button { width:40px; height:40px; border-radius:6px; border:1px solid #eceff1; background:#fff; font-weight:600; font-size:18px; cursor:pointer; }
          .pd-buy { width:100%; padding:12px 16px; border-radius:8px; border:none; background:#0b63d6; color:#fff; font-weight:700; font-size:16px; cursor:pointer; }
          .pd-section { margin-top:18px; }
          .pd-features ul { padding-left:18px; margin:6px 0; }
          .pd-variations { margin-bottom:16px; }
          .pd-variation-group { margin-bottom:16px; }
          .pd-variation-label { font-weight:600; margin-bottom:8px; display:block; font-size: 16px; color: #374151; }
          .pd-variation-buttons { display:flex; flex-wrap:wrap; gap:8px; }
          .pd-variation-btn { 
            padding:12px 16px; 
            border:2px solid #e1e5e9; 
            border-radius:8px; 
            background:#fff; 
            color:#374151; 
            font-weight:500; 
            cursor:pointer; 
            transition:all 0.2s ease; 
            text-align:center;
            min-width:80px;
          }
          .pd-variation-btn:hover { border-color:#0b63d6; background:#f8fafc; }
          .pd-variation-btn.selected { 
            border-color:#0b63d6; 
            background:#0b63d6; 
            color:#fff; 
            box-shadow:0 2px 4px rgba(11,99,214,0.2);
          }
          .pd-variation-name { font-size:14px; font-weight:600; }
          .pd-variation-price { font-size:12px; color:#6b7280; margin-top:2px; }
          .pd-variation-btn.selected .pd-variation-price { color:#e0f2fe; }
          .pd-original-price { text-decoration: line-through; color:#9ca3af; margin-right:4px; }
          .pd-discounted-price { color:#ef4444; font-weight:600; }
          @media (max-width: 992px) { .pd-grid { flex-direction:column; } .pd-left,.pd-right{flex:1 1 auto;} }
        `}</style>

        <Breadcrumb title="Product Details" items={[{ label: 'Home', href: '/' }, { label: 'Product Details' }]} />
        <section className="product__details--section pd-wrap">
          <div className="container">
            {loading ? (
              <div className="row"><div className="col-12"><div style={{ padding: 40, textAlign: 'center' }}>Loading product details...</div></div></div>
            ) : error ? (
              <div className="row"><div className="col-12"><div style={{ padding: 40, color: 'red', textAlign: 'center' }}>Error: {error}</div></div></div>
            ) : product ? (
              <div className="pd-grid">
                <div className="pd-left pd-card">
                  <img className="pd-image" src={selectedImage} alt={product.name} />
                  <div className="pd-thumbs">
                    {/* Show cover + images thumbnails */}
                    {product.cover && (
                      <img 
                        className="pd-thumb" 
                        src={product.cover.startsWith('http') ? product.cover : `${API_CONFIG.IMAGE_URL}${product.cover}`}
                        alt="Main"
                        onClick={() => setSelectedImage(product.cover.startsWith('http') ? product.cover : `${API_CONFIG.IMAGE_URL}${product.cover}`)}
                      />
                    )}
                    {product.images && product.images.length > 0 && product.images.map((img, idx) => {
                      if (!img || !img.trim()) return null;
                      const imgUrl = img.startsWith('http') ? img : `${API_CONFIG.IMAGE_URL}${img}`;
                      return (
                        <img 
                          key={idx} 
                          className="pd-thumb" 
                          src={imgUrl}
                          alt={`View ${idx + 2}`}
                          onClick={() => setSelectedImage(imgUrl)}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="pd-right pd-card">
                  <h1 className="pd-title">
                    {product.name}
                    {selectedVariation && (
                      <span className="pd-selected-variation"> - {selectedVariation.name}</span>
                    )}
                  </h1>
                  <div className="pd-price-row">
                    <div>
                      {selectedVariation && product.discount && product.discount > 0 ? (
                        <>
                          <div className="pd-price">${((selectedVariation.price * (1 - product.discount / 100))).toFixed(2)}</div>
                          <div className="pd-old">${selectedVariation.price.toFixed(2)}</div>
                        </>
                      ) : selectedVariation ? (
                        <div className="pd-price">${selectedVariation.price.toFixed(2)}</div>
                      ) : product.discount && product.discount > 0 ? (
                        <>
                          <div className="pd-price">${((product.price * (1 - product.discount / 100))).toFixed(2)}</div>
                          <div className="pd-old">${product.price.toFixed(2)}</div>
                        </>
                      ) : (
                        <>
                          <div className="pd-price">${product.price.toFixed(2)}</div>
                          {product.oldPrice && <div className="pd-old">${product.oldPrice.toFixed(2)}</div>}
                        </>
                      )}
                    </div>
                    <div style={{marginTop:8, fontSize:'16px', color:'#000', fontWeight:'bold'}}>
                      Total: {
                        (() => {
                          const unitPrice = selectedVariation && product.discount && product.discount > 0
                            ? selectedVariation.price * (1 - product.discount / 100)
                            : selectedVariation
                            ? selectedVariation.price
                            : product.discount && product.discount > 0
                            ? product.price * (1 - product.discount / 100)
                            : product.price;
                          return '$' + (unitPrice * quantity).toFixed(2);
                        })()
                      }
                    </div>
                  </div>

                  {variations && variations.length > 0 && variations.some(g => g.items && g.items.length > 0) && (
                    <div className="pd-section pd-variations">
                      {variations.map((variationGroup, groupIndex) => {
                        // Only render if this group has items
                        if (!variationGroup.items || variationGroup.items.length === 0) return null;
                        
                        return (
                          <div key={groupIndex} className="pd-variation-group">
                            <label className="pd-variation-label">{variationGroup.name}</label>
                            <div className="pd-variation-buttons">
                              {variationGroup.items.map((item, itemIndex) => {
                                // Variations can have their own discount field
                                const varDiscount = parseFloat(item.discount) || 0;
                                const varPrice = parseFloat(item.price) || 0;
                                const hasVarDiscount = varDiscount > 0;
                                
                                return (
                                  <button
                                    key={itemIndex}
                                    type="button"
                                    className={`pd-variation-btn ${selectedVariation && selectedVariation.id === item.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedVariation(item)}
                                  >
                                    <div className="pd-variation-name">{item.name}</div>
                                    <div className="pd-variation-price">
                                      {hasVarDiscount ? (
                                        <>
                                          <span className="pd-discounted-price">${varDiscount.toFixed(2)}</span>
                                          <span className="pd-original-price">${varPrice.toFixed(2)}</span>
                                        </>
                                      ) : (
                                        <span>${varPrice.toFixed(2)}</span>
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div>
                    {Number(product.in_stock) === 1 ? (
                      <span className="pd-available" style={{color:'#28a745'}}>Available</span>
                    ) : (
                      <span className="pd-available" style={{color:'#dc3545'}}>Out of stock</span>
                    )}
                    {((Number(product.have_pcs) === 1) && product.pcs && product.pcs !== "0") ? (
                      <span className="pd-measure">{product.pcs} pcs</span>
                    ) : ((Number(product.have_kg) === 1) && product.kg && product.kg !== "0") ? (
                      <span className="pd-measure">{product.kg} kg</span>
                    ) : ((Number(product.have_gram) === 1) && product.gram && product.gram !== "0") ? (
                      <span className="pd-measure">{product.gram} g</span>
                    ) : ((Number(product.have_liter) === 1) && product.liter && product.liter !== "0") ? (
                      <span className="pd-measure">{product.liter} L</span>
                    ) : ((Number(product.have_ml) === 1) && product.ml && product.ml !== "0") ? (
                      <span className="pd-measure">{product.ml} ml</span>
                    ) : null}
                  </div>

                  <div className="pd-section" style={{display:'flex', gap:12, alignItems:'center'}}>
                    {isInCart && cartItem ? (
                      <div className="pd-qty">
                        <button type="button" onClick={() => {
                          const newQuantity = cartItem.quantiy - 1;
                          if (newQuantity <= 0) {
                            cart.updateQuantity(product.id, 0); // Remove
                          } else {
                            cart.updateQuantity(product.id, newQuantity);
                          }
                        }}>-</button>
                        <div style={{minWidth:28, textAlign:'center', fontWeight:600}}>{cartItem.quantiy}</div>
                        <button type="button" onClick={() => cart.updateQuantity(product.id, cartItem.quantiy + 1)}>+</button>
                      </div>
                    ) : (
                      <>
                        <div className="pd-qty">
                          <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
                          <div style={{minWidth:28, textAlign:'center', fontWeight:600}}>{quantity}</div>
                          <button type="button" onClick={() => setQuantity(q => Math.min(99, q + 1))}>+</button>
                        </div>
                        <button 
                          className="pd-buy" 
                          onClick={async () => {
                            if (product) {
                              try {
                                // Calculate final price with discount
                                const basePrice = selectedVariation ? selectedVariation.price : product.price;
                                const finalPrice = product.discount && product.discount > 0 
                                  ? (basePrice * (1 - product.discount / 100)) 
                                  : basePrice;
                                
                                // Create cart item with the selected quantity and variation
                                const cartItem = {
                                  ...product,
                                  quantiy: quantity,
                                  selectedVariation: selectedVariation,
                                  price: finalPrice,
                                  originalPrice: basePrice,
                                  cover: (() => {
                                    if (product.cover && typeof product.cover === 'string' && product.cover.trim()) {
                                      return product.cover;
                                    }
                                    if (product.image && typeof product.image === 'string' && product.image.trim()) {
                                      return product.image;
                                    }
                                    if (product.images) {
                                      if (Array.isArray(product.images) && product.images.length > 0) {
                                        const firstImg = product.images[0];
                                        return typeof firstImg === 'string' && firstImg.trim() ? firstImg : '';
                                      }
                                      if (typeof product.images === 'string') {
                                        try {
                                          const parsed = JSON.parse(product.images);
                                          if (Array.isArray(parsed) && parsed.length > 0) {
                                            const firstImg = parsed[0];
                                            return typeof firstImg === 'string' && firstImg.trim() ? firstImg : '';
                                          }
                                        } catch (e) {}
                                      }
                                    }
                                    return '';
                                  })()
                                };
                                await cart.addItem(cartItem);
                                console.log('Product added to cart');
                              } catch (error) {
                                console.error('Failed to add product to cart:', error);
                              }
                            }
                          }}
                        >
                          Add to Cart
                        </button>
                      </>
                    )}
                    <button
                      className="pd-buy"
                      style={{ background: wishlist.isInWishlist(product?.id) ? '#e63946' : '#fff', color: wishlist.isInWishlist(product?.id) ? '#fff' : '#0b63d6', border: '1px solid #e63946' }}
                      onClick={() => {
                        if (wishlist.isInWishlist(product?.id)) {
                          wishlist.removeFromWishlist(product?.id);
                        } else {
                          wishlist.addToWishlist(product);
                        }
                      }}
                    >
                      {wishlist.isInWishlist(product?.id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                    </button>
                  </div>

                  <div className="pd-section">
                    <h4 style={{marginBottom:8}}>Description</h4>
                    <div style={{color:'#444'}}>{product.description}</div>
                  </div>

                  {product.key_features && (
                    <div className="pd-section pd-features">
                      <h4 style={{marginBottom:8}}>Key features</h4>
                      {(() => {
                        const features = typeof product.key_features === 'string' 
                          ? product.key_features.split(/,|\n/).map(f => f.trim()).filter(f => f) 
                          : [product.key_features].filter(f => f);
                        return (
                          <ul>
                            {features.map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        );
                      })()}
                    </div>
                  )}

                  {product.disclaimer && (
                    <div className="pd-section">
                      <h4 style={{marginBottom:8}}>Disclaimer</h4>
                      <div style={{color:'#666'}}>{product.disclaimer}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {product && product.related && product.related.length > 0 && (
          <section className="product__section product__section--style3 section--padding pt-0">
            <div className="container-fluid">
              <div className="section__heading3 mb-40">
                <h2 className="section__heading3--maintitle">Related Products</h2>
              </div>
              <div className="product__section--inner product3__section--inner__padding product__section--style3__inner product__column6--activation swiper">
                <div className="swiper-wrapper">
                  <Swiper
                    modules={[Navigation]}
                    spaceBetween={30}
                    slidesPerView={4}
                    navigation={{
                      nextEl: '.swiper-button-next',
                      prevEl: '.swiper-button-prev',
                    }}
                    breakpoints={{
                      320: { slidesPerView: 1 },
                      480: { slidesPerView: 2 },
                      768: { slidesPerView: 3 },
                      992: { slidesPerView: 4 },
                    }}
                  >
                    {product.related.map((relatedProduct) => (
                      <SwiperSlide key={relatedProduct.id}>
                        <ProductCard product={relatedProduct} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* <ShippingInfo /> removed as per request */}
      </main>
      <Footer />
    </>
  );
};

export default ProductDetailPage;
